/* side-menu.js
 * Höfats-style off-canvas left drawer with exclusive accordion panels.
 *
 * The <side-menu> custom element extends Dawn's HeaderDrawer (which itself
 * extends MenuDrawer in assets/global.js) so it inherits:
 *   - focus trap while open
 *   - ESC key to close
 *   - body scroll lock via the overflow-hidden-{breakpoint} class
 *   - --header-bottom-position and --viewport-height CSS property management
 *   - the menu-opening class transition + close animation
 *
 * On top of that it adds:
 *   - exclusive accordion behaviour — opening one top-level <details> panel
 *     closes all other sibling panels at the same level
 *   - aria-expanded syncing on every panel summary
 *
 * We override bindEvents() so the inherited MenuDrawer summary-click handler
 * only manages the MAIN drawer toggle (the hamburger). Inner accordion panels
 * are handled by our own onPanelSummaryClick() with exclusive-close logic,
 * avoiding the conflict of two handlers both toggling the same <details>.
 *
 * The no-JS fallback is the native <details>/<summary> behaviour (every panel
 * independently toggles, the drawer opens without animation). Reduced motion
 * disables transitions via CSS in component-side-menu.css.
 */

if (typeof HeaderDrawer === 'undefined') {
  // global.js (which defines HeaderDrawer) failed to load or runs after this
  // file. Fall back to a plain custom element so the native <details> drawer
  // still works without the JS enhancements (exclusive accordion, focus trap).
  class SideMenuFallback extends HTMLElement {
    constructor() {
      super();
    }
  }
  customElements.define('side-menu', SideMenuFallback);
} else {
  class SideMenu extends HeaderDrawer {
    constructor() {
      super();
    }

    /* Override MenuDrawer.bindEvents so the inherited onSummaryClick handler
       is attached ONLY to the main hamburger summary, not to the inner panel
       summaries. Inner panels get onPanelSummaryClick instead. */
    bindEvents() {
      // Main drawer toggle (the hamburger) — let MenuDrawer handle open/close.
      const mainSummary = this.mainDetailsToggle
        ? this.mainDetailsToggle.querySelector(':scope > summary')
        : null;
      if (mainSummary) {
        mainSummary.addEventListener('click', this.onSummaryClick.bind(this));
      }

      // Inner accordion panels — exclusive accordion handled here.
      this.panels = this.querySelectorAll('.side-menu__panel');
      this.panels.forEach((panel) => {
        const summary = panel.querySelector(':scope > .side-menu__panel-summary');
        if (!summary) return;
        summary.addEventListener('click', this.onPanelSummaryClick.bind(this));
        // NOTE: panels deliberately do NOT auto-close on mouseleave. They open
        // on an explicit click and close on an explicit action — clicking the
        // summary again, opening another panel (exclusive accordion), or
        // clicking outside the menu (onOutsideClick). A former mouseleave
        // handler collapsed an expanded panel the instant the cursor drifted
        // off its box, which made the drawer's accordion panels nearly
        // impossible to use. At >=1200px the horizontal nav's pure-CSS :hover
        // rule (component-side-menu.css) still reveals/hides on hover on its
        // own, with no JS needed.
      });

      // Close buttons (none in side-menu, but kept for safety with the
      // inherited onCloseButtonClick which expects .has-submenu structure).
      // The login form's submit button is excluded so submitting it doesn't
      // also trigger a panel-close side effect right before navigation.
      this.querySelectorAll(
        'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.side-menu__login-submit):not(.side-menu__country-trigger):not(.side-menu__country-modal-close):not(.side-menu__country-modal-item)'
      ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

      // Locale links (Language panel) — flat list, single click submits the
      // localization form directly instead of Dawn's nested disclosure
      // button, matching the reference site's one-click behaviour.
      this.querySelectorAll('.side-menu__locale-link').forEach((link) => {
        link.addEventListener('click', this.onLocaleLinkClick.bind(this));
      });

      // Tap-to-close on tablet/desktop: at 750px+ the panels above render as
      // hover/tap mega-dropdowns (see component-side-menu.css), driven by
      // the [open] attribute this class already toggles. Mouse users get a
      // free close on mouseleave via CSS :hover, but touch has no hover
      // state, so a panel opened by tapping its label needs an explicit way
      // to close again — tapping anywhere outside the menu does that.
      document.addEventListener('click', this.onOutsideClick.bind(this));

      // Country selector — a centered "choose your country" popup (matches
      // the reference site) instead of Dawn's stock corner/inline disclosure
      // dropdown. Independent of the accordion/hover-panel system above:
      // it's a single dialog shared by every breakpoint, toggled via a
      // `hidden` attribute rather than native <details>.
      this.countryModal = this.querySelector('.side-menu__country-modal');
      this.querySelectorAll('[data-country-modal-open]').forEach((trigger) => {
        trigger.addEventListener('click', this.onCountryModalOpen.bind(this));
      });
      if (this.countryModal) {
        this.countryModal.querySelectorAll('[data-country-modal-close]').forEach((el) => {
          el.addEventListener('click', this.onCountryModalClose.bind(this));
        });
        this.countryModal.querySelectorAll('.side-menu__country-modal-item').forEach((item) => {
          item.addEventListener('click', this.onCountrySelect.bind(this));
        });
      }
    }

    onCountryModalOpen(event) {
      event.preventDefault();
      if (!this.countryModal) return;
      this.countryModal.removeAttribute('hidden');
      // `.side-menu` has `filter: drop-shadow(...)`, which traps our fixed
      // modal inside the drawer's box on mobile (see component-side-menu.css)
      // — reset it for as long as the modal is open, same rescue Dawn's own
      // country selector needed.
      const drawer = this.querySelector('.side-menu');
      if (drawer) drawer.classList.add('country-selector-open');
      // Double rAF so the removed [hidden] has actually applied display:flex
      // before we add the class that transitions opacity/transform — avoids
      // the transition being skipped because both changes landed in the same
      // paint frame.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.countryModal.classList.add('is-open');
        });
      });
      this.countryModalOpener = event.currentTarget;
      document.addEventListener('keydown', this.onCountryModalKeydown);
    }

    onCountryModalClose() {
      if (!this.countryModal) return;
      this.countryModal.classList.remove('is-open');
      document.removeEventListener('keydown', this.onCountryModalKeydown);
      const drawer = this.querySelector('.side-menu');
      const finish = () => {
        this.countryModal.setAttribute('hidden', '');
        if (drawer) drawer.classList.remove('country-selector-open');
      };
      // Wait for the CSS transition before actually hiding, so the fade-out
      // is visible instead of the dialog just vanishing instantly.
      this.countryModal.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 250);
      if (this.countryModalOpener) this.countryModalOpener.focus();
    }

    onCountryModalKeydown = (event) => {
      if (event.key === 'Escape') this.onCountryModalClose();
    };

    onCountrySelect(event) {
      const button = event.currentTarget;
      const form = button.closest('form');
      if (!form) return;
      const input = form.querySelector('input[name="country_code"]');
      if (input) input.value = button.dataset.value;
      form.submit();
    }

    onOutsideClick(event) {
      if (this.contains(event.target)) return;
      this.closeAllPanels();
    }

    closeAllPanels() {
      if (!this.panels) return;
      this.panels.forEach((panel) => {
        if (!panel.hasAttribute('open')) return;
        panel.removeAttribute('open');
        const summary = panel.querySelector(':scope > .side-menu__panel-summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      });
    }

    onLocaleLinkClick(event) {
      event.preventDefault();
      const link = event.currentTarget;
      const form = link.closest('form');
      if (!form) return;
      const input = form.querySelector('input[name="locale_code"]');
      if (input) input.value = link.dataset.value;
      form.submit();
    }

    /* Override HeaderDrawer.openMenuDrawer to safely handle the missing
       .header-wrapper element (side-menu is a sibling section, not inside
       the header). */
    openMenuDrawer(summaryElement) {
      this.header = this.header || document.querySelector('.section-header');
      const headerWrapper = this.closest('.header-wrapper');
      this.borderOffset = this.borderOffset || (headerWrapper && headerWrapper.classList.contains('header-wrapper--border-bottom') ? 1 : 0);

      setTimeout(() => {
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${(this.header?.offsetHeight || 0) + (this.borderOffset || 0)}px`
        );
      });

      this.header?.classList.add('menu-open');

      // Call MenuDrawer's openMenuDrawer (not HeaderDrawer's, since we handled the header stuff).
      MenuDrawer.prototype.openMenuDrawer.call(this, summaryElement);
    }

    /* Override HeaderDrawer.closeMenuDrawer to safely remove the menu-open
       class from the header. */
    closeMenuDrawer(event, elementToFocus) {
      this.header?.classList.remove('menu-open');
      MenuDrawer.prototype.closeMenuDrawer.call(this, event, elementToFocus);
    }

    /* Exclusive accordion: when a panel summary is clicked, close all other
       open sibling panels, then toggle the clicked one. We preventDefault to
       own the <details> open attribute (avoids the native toggle racing our
       exclusive-close logic). */
    onPanelSummaryClick(event) {
      // Panel labels are plain text, never links — clicking one only ever
      // opens or closes its dropdown. The page a panel points at is reached
      // through the "See all" link at the foot of the panel instead
      // (block.settings.link in side-menu.liquid).
      const summary = event.currentTarget;
      const panel = summary.parentNode;
      const isOpen = panel.hasAttribute('open');

      event.preventDefault();

      // Close every other open panel (exclusive accordion).
      this.panels.forEach((other) => {
        if (other === panel) return;
        if (other.hasAttribute('open')) {
          other.removeAttribute('open');
          const otherSummary = other.querySelector(':scope > .side-menu__panel-summary');
          if (otherSummary) otherSummary.setAttribute('aria-expanded', 'false');
        }
      });

      // Toggle the clicked panel.
      if (isOpen) {
        panel.removeAttribute('open');
        summary.setAttribute('aria-expanded', 'false');
      } else {
        panel.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
      }
    }
  }

  customElements.define('side-menu', SideMenu);
}

/* ---------------------------------------------------------------------
   Desktop nav reserved-space measurement (>=1200px horizontal bar, see
   component-side-menu.css). The bar overlays the full header row and
   must clear the header's own logo + account/cart icons underneath,
   which live in header.liquid's markup, not this section's. A static
   px/rem guess drifts every time the logo width, locale selectors, or
   icon set changes — measure the real gap instead: the distance from
   the header's right edge back to the logo link's left edge IS exactly
   the space to reserve, whatever produced it. */
(function () {
  var desktopQuery = window.matchMedia('(min-width: 1200px)');

  function updateReservedWidth() {
    if (!desktopQuery.matches) return;
    var header = document.querySelector('.header');
    var logoLink = document.querySelector('.header__heading-link');
    if (!header || !logoLink) return;
    var reserved = header.getBoundingClientRect().right - logoLink.getBoundingClientRect().left;
    if (reserved > 0) {
      document.documentElement.style.setProperty('--header-reserved-right', reserved + 'px');
    }
  }

  var rafId = null;
  function scheduleUpdate() {
    if (rafId) return;
    rafId = requestAnimationFrame(function () {
      rafId = null;
      updateReservedWidth();
    });
  }

  window.addEventListener('resize', scheduleUpdate);
  desktopQuery.addEventListener('change', scheduleUpdate);
  window.addEventListener('load', scheduleUpdate);
  if (document.readyState === 'complete') {
    updateReservedWidth();
  } else {
    document.addEventListener('DOMContentLoaded', updateReservedWidth);
  }

  // The logo image can still be loading when the passes above run, which
  // would measure its wrapper before it has its natural box — re-measure
  // once it actually finishes loading.
  var logoImg = document.querySelector('.header__heading-logo');
  if (logoImg && !logoImg.complete) {
    logoImg.addEventListener('load', updateReservedWidth, { once: true });
  }
})();